import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  TextField,
} from "@mui/material";
import styles from "./TentHouseService.module.css";
import {
  getTentHouseProviderServices,
  getTentHouseProviderAddedServices,
  syncTentHouseProviderServices,
} from "../../Apiservices/Auth/servers/fetchProviderData";
import ThButton from "../../redesign/components/ThButton/ThButton";

function TentHouseService(props) {
  const INDICATOR_CONFIG = {
    highlightRow: true, // Highlights the entire row if `toSave` is true
    highlightCell: false, // Highlights individual cells that have been changed
    showRedDot: false, // Shows a red dot (🔴) before service name if `toSave` is true
    showUnsavedLabel: true, // Displays "(Unsaved)" next to service name if `toSave` is true
  };
  const [gridData, setGridData] = useState([]);
  const [filteredGridData, setFilteredGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  // Fetch services data
  const resultedServicesFunc = useCallback(async () => {
    setLoading(true);
    try {
      const tentHouseServices = await getTentHouseProviderServices(); // All services
      const addedServices = await getTentHouseProviderAddedServices(); // Services that are already mapped
      const tempGridData = tentHouseServices.map((service) => {
        const existingService = addedServices.find(
          (added) => added.tent_house_service_id === service.id
        );
        return {
          id: service.id,
          tent_house_service_id: service.id,
          service_name: service.service_name,
          description: service.description,
          eventMappingId: existingService?.eventMappingId,
          price: existingService ? existingService.price : null,
          discount_price: existingService
            ? existingService.discount_price
            : null,
          discount_valid_upto: existingService
            ? existingService.discount_valid_upto
              ? new Date(existingService.discount_valid_upto)
              : null
            : null,
          toSave: false,
          alreadyMapped: !!existingService,
          errorMessage: "",
        };
      });

      tempGridData.sort((a, b) => {
        if (a.alreadyMapped === b.alreadyMapped) {
          return a.service_name.localeCompare(b.service_name);
        }
        return a.alreadyMapped ? -1 : 1;
      });

      setGridData(tempGridData);
      setFilteredGridData(tempGridData);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    resultedServicesFunc();
  }, []);
  const handleInputChange = (id, field, value) => {
    // Update gridData first
    const updatedGridData = gridData.map((item) => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value,
          toSave: true,
          changedFields: [...new Set([...(item.changedFields || []), field])],
          errorMessage: "", // Clear previous errors
        };
        // Error handling: If Discount Price or Valid Upto is entered but no Price, show error
        if (
          field === "discount_price" &&
          value &&
          Number(value) > Number(updatedItem.price)
        ) {
          updatedItem.errorMessage =
            "Discount price must be less than or equal to price.";
        }
        if (
          field === "price" &&
          updatedItem.discount_price &&
          Number(updatedItem.discount_price) > Number(value)
        ) {
          updatedItem.errorMessage =
            "Discount price must be less than or equal to price.";
        }
        // Date validation: Discount valid date should not be in the past
        if (field === "discount_valid_upto" && value) {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Set time to start of the day for proper comparison
          if (selectedDate < today) {
            updatedItem.errorMessage =
              "Discount valid date cannot be in the past.";
          }
        }


        if (updatedItem.discount_valid_upto && !updatedItem.discount_price) {
          updatedItem.errorMessage =
            "Please enter a discount price if discount valid date is set.";
        }
        if (updatedItem.discount_price && !updatedItem.price) {
          updatedItem.errorMessage = "Please enter a price for discount to apply.";
        }
        return updatedItem;
      }
      return item;
    });
    setGridData(updatedGridData);
    // Apply filter again to update filteredGridData
    const updatedFilteredData = updatedGridData.filter(
      (service) =>
        service.service_name.toLowerCase().includes(searchTerm) ||
        service.description.toLowerCase().includes(searchTerm)
    );

    setFilteredGridData(updatedFilteredData);
    setHasChanges(updatedGridData.some((service) => service.toSave));
    setHasErrors(updatedGridData.some((service) => service.errorMessage));
  };

  // Reset a row
  const handleRemoveService = (id) => {
    const tempUpdatedGridData = gridData.map((service) =>
      service.id === id
        ? {
            ...service,
            price: null,
            discount_price: null,
            discount_valid_upto: null,
            toSave: true, 
            changedFields: ["price", "discount_price", "discount_valid_upto"],
          }
        : service
    );

    setGridData(tempUpdatedGridData);
    setHasChanges(true);

    // Apply filter again to update filteredGridData
    const updatedFilteredData = tempUpdatedGridData.filter(
      (service) =>
        service.service_name.toLowerCase().includes(searchTerm) ||
        service.description.toLowerCase().includes(searchTerm)
    );

    setFilteredGridData(updatedFilteredData);
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    if (value === "") {
      setFilteredGridData(gridData);
    } else {
      setFilteredGridData(
        gridData.filter(
          (service) =>
            service.service_name.toLowerCase().includes(value) ||
            service.description.toLowerCase().includes(value)
        )
      );
    }
  };

  const handleSaveChanges = async () => {
    if (hasErrors) return;

    const changesToSave = filteredGridData.filter((service) => service.toSave);
    try {
      await syncTentHouseProviderServices(changesToSave);
      setHasChanges(false);
      await resultedServicesFunc();
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  // Show loading spinner while fetching
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <main className={styles.mainContent}>
      <div className={styles.searchWidget}>
        <label className={styles.searchLabel}>Item list</label>
        <TextField
          fullWidth
          label="Search Items"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            mb: 0,
            backgroundColor: "#FFF",
            fontFamily: "Inter",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "24px",
            "& .MuiInputBase-input": {
              color: "#888", // default text color
              fontFamily: "Inter",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px",
            },
            "& .MuiInputBase-root.Mui-focused .MuiInputBase-input": {
              color: "#383838", // text color on focus
            },
            "& .MuiInputLabel-root": {
              color: "#888", // label default
              fontFamily: "Inter",
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#383838", // label color on focus
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#FFF",
              "& fieldset": {
                borderColor: "#D7D7D7",
              },
              "&:hover fieldset": {
                borderColor: "#383838",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#383838",
              },
            },
          }}
        />
      </div>
      <div className={styles.buttonFrameRow}>
        <div className={styles.errorNotice}>
          {hasErrors ? "Please resolve all errors" : ""}
        </div>

        <div>
          <ThButton
            inactive={hasErrors || !hasChanges}
            onClick={handleSaveChanges}
          >
            Save Changes
          </ThButton>
        </div>
      </div>

      <div className={styles.horizontalScrollWrapper}>
        <div className={styles.gridTable}>
          <div className={styles.gridHeader}>
            <div className={styles.gridCell}>Item Name</div>
            <div className={styles.gridCell}>Item Price*</div>
            <div className={styles.gridCell}>{`Discount (Flat OFF)`}</div>
            <div className={styles.gridCell}>Discount Valid Upto</div>
            <div className={styles.gridCell}>Action</div>
          </div>
          <div className={styles.verticalScrollWrapper}>
            {filteredGridData.map((row) => {
              console.log(row);
              return (
                <div key={row.id} className={styles.gridRow}>
                  <div className={styles.gridCell}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div>{row.service_name}</div>
                      {row.errorMessage && (
                        <div
                          style={{
                            color: "red",
                            fontSize: "10px",
                            marginTop: "0px",
                          }}
                        >
                          {row.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.gridCell}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Enter price"
                      value={row.price || ""}
                      onChange={(e) =>
                        handleInputChange(row.id, "price", e.target.value)
                      }
                      InputProps={{
                        startAdornment: (
                          <span
                            style={{
                              width: "9px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              marginRight: "8px",
                              color: "#383838",
                              fontSize: "14px",
                              fontWeight: 400,
                              fontFamily: "Inter",
                              lineHeight: "24px",
                            }}
                          >
                            ₹
                          </span>
                        ),
                        sx: {
                          height: "40px",
                          borderRadius: "8px",
                          border: "1px solid #E9E9E9",
                          backgroundColor: "#FFF",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "24px",
                          color: "#383838",
                          borderRadius: "8px",
                          backgroundColor: "#FFF",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E9E9E9",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#BDBDBD", // optional hover color
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E9E9E9", // override blue focus
                          },
                          padding: "0 14px",
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                        },
                      }}
                      inputProps={{
                        style: {
                          boxSizing: "border-box",
                          MozAppearance: "textfield",
                          WebkitAppearance: "none",
                          padding: "8px 0",
                          height: "24px",
                          color: row.price ? "#383838" : "#888",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "24px",
                        },
                      }}
                    />
                  </div>
                  <div className={styles.gridCell}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Enter price"
                      value={row.discount_price || ""}
                      onChange={(e) =>
                        handleInputChange(
                          row.id,
                          "discount_price",
                          e.target.value
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <span
                            style={{
                              width: "9px",
                              height: "24px",
                              display: "flex",
                              alignItems: "center",
                              marginRight: "8px",
                              color: "#383838",
                              fontSize: "14px",
                              fontWeight: 400,
                              fontFamily: "Inter",
                              lineHeight: "24px",
                            }}
                          >
                            ₹
                          </span>
                        ),
                        sx: {
                          height: "40px",
                          borderRadius: "8px",
                          border: "1px solid #E9E9E9",
                          backgroundColor: "#FFF",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "24px",
                          color: "#383838",
                          padding: "0 14px",
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          borderRadius: "8px",
                          backgroundColor: "#FFF",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E9E9E9",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#BDBDBD", // optional hover color
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E9E9E9", // override blue focus
                          },
                        },
                      }}
                      inputProps={{
                        style: {
                          boxSizing: "border-box",
                          MozAppearance: "textfield",
                          WebkitAppearance: "none",
                          padding: "8px 0",
                          height: "24px",
                          color: row.price ? "#383838" : "#888",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "24px",
                        },
                      }}
                    />
                  </div>
                  <div className={styles.gridCell}>
                    <TextField
                      type="date"
                      variant="outlined"
                      size="small"
                      value={
                        row.discount_valid_upto &&
                        !isNaN(new Date(row.discount_valid_upto).getTime())
                          ? new Date(row.discount_valid_upto)
                              .toISOString()
                              .split("T")[0]
                          : undefined
                      }
                      onChange={(e) =>
                        handleInputChange(
                          row.id,
                          "discount_valid_upto",
                          e.target.value
                        )
                      }
                      InputProps={{
                        sx: {
                          borderRadius: "8px",
                          backgroundColor: "#FFF",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E9E9E9",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#BDBDBD", 
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E9E9E9",
                          },
                        },
                      }}
                      inputProps={{
                        min: new Date().toISOString().split("T")[0],
                        style: {
                          color: row.discount_valid_upto ? "#383838" : "#888",
                        },
                      }}
                    />
                  </div>
                  <div className={styles.gridCell}>
                    <Button
                      onClick={() => handleRemoveService(row.id)}
                      variant="outlined"
                      color="error"
                      disabled={!row.toSave && !row.alreadyMapped}
                      sx={{
                        padding: "8px",
                        textTransform: "uppercase", // Uppercase text
                        color:
                          !row.toSave && !row.alreadyMapped
                            ? "#888"
                            : "#383838", 
                        borderColor:
                          !row.toSave && !row.alreadyMapped
                            ? "#888"
                            : "#383838", 
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "24px",
                        cursor:
                          !row.toSave && !row.alreadyMapped
                            ? "not-allowed"
                            : "pointer", 
                        "&:hover": {
                          borderColor:
                            !row.toSave && !row.alreadyMapped
                              ? "#888"
                              : "#383838", 
                        },
                      }}
                    >
                      REMOVE
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </main>
  );
}

export default TentHouseService;
